import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import EditIcon from '@mui/icons-material/Edit';
import { FormControlLabel, Switch } from '@mui/material';
import toast from 'react-hot-toast';
import api from '../services/api';

interface AdminStats {
  users: { total: number; verified: number; blocked: number };
  subscriptions: {
    active: number;
    paid: number;
    trials: number;
    byPlan: Record<string, number>;
    mrr: number;
    trialConversion: number | null;
  };
  vkAccounts: { total: number; invalidTokens: number };
  activity: { actions24h: number; actions7d: number; activeConfigs: number };
}

interface AdminUser {
  id: number;
  email: string;
  username: string;
  role: string;
  isVerified: boolean;
  isBlocked: boolean;
  createdAt: string;
  vkAccounts: number;
  configs: number;
  subscription: {
    planCode: string;
    planName: string;
    status: string;
    isTrial: boolean;
    periodEnd: string;
  } | null;
}

interface QueueInfo {
  name: string;
  counts: Record<string, number>;
}

function StatCard({ title, value, hint }: { title: string; value: string | number; hint?: string }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5">{value}</Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState(0);

  // Обзор
  const [stats, setStats] = useState<AdminStats | null>(null);

  // Пользователи
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Платежи
  const [payments, setPayments] = useState<any[]>([]);

  // Система
  const [queues, setQueues] = useState<QueueInfo[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  // Диалог выдачи подписки
  const [grantUser, setGrantUser] = useState<AdminUser | null>(null);
  const [grantPlan, setGrantPlan] = useState('pro');
  const [grantMonths, setGrantMonths] = useState(1);

  // Диалог редактирования пользователя
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    role: 'user',
    isVerified: false,
    isBlocked: false,
  });
  const openEdit = (u: AdminUser) => {
    setEditForm({
      username: u.username,
      email: u.email,
      role: u.role,
      isVerified: u.isVerified,
      isBlocked: u.isBlocked,
    });
    setEditUser(u);
  };
  const saveEdit = async () => {
    if (!editUser) return;
    await patchUser(editUser.id, editForm);
    setEditUser(null);
  };

  const loadStats = useCallback(() => {
    api.get('/admin/stats').then((res) => setStats(res.data.data));
  }, []);

  const loadUsers = useCallback(() => {
    api
      .get('/admin/users', { params: { search: search || undefined, page: page + 1, pageSize } })
      .then((res) => {
        setUsers(res.data.data.users);
        setUsersTotal(res.data.data.total);
      });
  }, [search, page]);

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats').then((res) => setStats(res.data.data)),
      api.get('/admin/payments').then((res) => setPayments(res.data.data.payments)),
      api.get('/admin/system').then((res) => setQueues(res.data.data.queues)),
    ])
      .catch(() => toast.error('Нет доступа к админке'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const patchUser = async (userId: number, data: Record<string, unknown>) => {
    try {
      await api.patch(`/admin/users/${userId}`, data);
      toast.success('Сохранено');
      loadUsers();
      loadStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка');
    }
  };

  const grantSubscription = async () => {
    if (!grantUser) return;
    try {
      await api.post(`/admin/users/${grantUser.id}/subscription`, {
        planCode: grantPlan,
        months: grantMonths,
      });
      toast.success('Подписка выдана');
      setGrantUser(null);
      loadUsers();
      loadStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Админка
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Обзор" />
        <Tab label="Пользователи" />
        <Tab label="Платежи" />
        <Tab label="Система" />
      </Tabs>

      {tab === 0 && stats && (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <StatCard
              title="Пользователи"
              value={stats.users.total}
              hint={`подтверждено ${stats.users.verified}, заблокировано ${stats.users.blocked}`}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              title="MRR"
              value={`${stats.subscriptions.mrr} ₽`}
              hint={`платных подписок: ${stats.subscriptions.paid}`}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              title="Активные подписки"
              value={stats.subscriptions.active}
              hint={Object.entries(stats.subscriptions.byPlan)
                .map(([name, count]) => `${name}: ${count}`)
                .join(', ')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              title="Триалы"
              value={stats.subscriptions.trials}
              hint={
                stats.subscriptions.trialConversion !== null
                  ? `конверсия в оплату ${stats.subscriptions.trialConversion}%`
                  : 'конверсия — нет данных'
              }
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              title="VK-аккаунты"
              value={stats.vkAccounts.total}
              hint={`битых токенов: ${stats.vkAccounts.invalidTokens}`}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Действий за 24 ч" value={stats.activity.actions24h} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Действий за 7 дней" value={stats.activity.actions7d} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Активных правил" value={stats.activity.activeConfigs} />
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Box>
          <TextField
            size="small"
            placeholder="Поиск по email / имени"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 2, width: 320 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Аккаунты / Правила</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.id}</TableCell>
                    <TableCell>
                      {u.email}
                      {u.role === 'admin' && (
                        <Chip label="admin" size="small" color="secondary" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {u.subscription ? (
                        <>
                          {u.subscription.planName}
                          {u.subscription.isTrial && (
                            <Chip label="триал" size="small" sx={{ ml: 0.5 }} />
                          )}
                          <Typography variant="caption" display="block" color="text.secondary">
                            до {new Date(u.subscription.periodEnd).toLocaleDateString('ru-RU')}
                          </Typography>
                        </>
                      ) : (
                        <Chip label="нет" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {u.vkAccounts} / {u.configs}
                    </TableCell>
                    <TableCell>
                      {u.isBlocked ? (
                        <Chip label="заблокирован" size="small" color="error" />
                      ) : u.isVerified ? (
                        <Chip label="активен" size="small" color="success" />
                      ) : (
                        <Chip label="не подтверждён" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Редактировать пользователя">
                        <IconButton size="small" onClick={() => openEdit(u)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!u.isVerified && (
                        <Tooltip title="Подтвердить email вручную">
                          <IconButton size="small" onClick={() => patchUser(u.id, { isVerified: true })}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Выдать/продлить подписку">
                        <IconButton size="small" onClick={() => setGrantUser(u)}>
                          <CardMembershipIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {u.isBlocked ? (
                        <Tooltip title="Разблокировать">
                          <IconButton size="small" onClick={() => patchUser(u.id, { isBlocked: false })}>
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Заблокировать (правила будут остановлены)">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (window.confirm(`Заблокировать ${u.email}?`)) {
                                patchUser(u.id, { isBlocked: true });
                              }
                            }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={usersTotal}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
          />
        </Box>
      )}

      {tab === 2 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Пользователь</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell>Тариф</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell>Оплачен</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                      Платежей пока нет — приём оплаты через Робокассу ещё не подключён
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>{p.user?.email}</TableCell>
                  <TableCell>
                    {p.amount} {p.currency}
                  </TableCell>
                  <TableCell>{p.subscription?.plan?.name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.status}
                      size="small"
                      color={p.status === 'succeeded' ? 'success' : p.status === 'failed' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{new Date(p.createdAt).toLocaleString('ru-RU')}</TableCell>
                  <TableCell>{p.paidAt ? new Date(p.paidAt).toLocaleString('ru-RU') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 3 && (
        <Grid container spacing={2}>
          {queues.map((q) => (
            <Grid item xs={12} md={6} key={q.name}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Очередь {q.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(q.counts).map(([state, count]) => (
                      <Chip
                        key={state}
                        label={`${state}: ${count}`}
                        size="small"
                        color={state === 'failed' && count > 0 ? 'error' : 'default'}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Редактировать пользователя #{editUser?.id}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Имя пользователя"
            size="small"
            value={editForm.username}
            onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
          />
          <TextField
            label="Email"
            size="small"
            value={editForm.email}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
          />
          <Select
            size="small"
            value={editForm.role}
            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
          >
            <MenuItem value="user">Пользователь</MenuItem>
            <MenuItem value="admin">Администратор</MenuItem>
          </Select>
          <FormControlLabel
            control={
              <Switch
                checked={editForm.isVerified}
                onChange={(e) => setEditForm({ ...editForm, isVerified: e.target.checked })}
              />
            }
            label="Email подтверждён"
          />
          <FormControlLabel
            control={
              <Switch
                checked={editForm.isBlocked}
                onChange={(e) => setEditForm({ ...editForm, isBlocked: e.target.checked })}
              />
            }
            label="Заблокирован"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Отмена</Button>
          <Button variant="contained" onClick={saveEdit}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!grantUser} onClose={() => setGrantUser(null)}>
        <DialogTitle>Выдать подписку — {grantUser?.email}</DialogTitle>
        <DialogContent sx={{ display: 'flex', gap: 2, pt: '8px !important' }}>
          <Select size="small" value={grantPlan} onChange={(e) => setGrantPlan(e.target.value)}>
            <MenuItem value="start">Старт</MenuItem>
            <MenuItem value="pro">Профи</MenuItem>
            <MenuItem value="business">Бизнес</MenuItem>
          </Select>
          <Select
            size="small"
            value={grantMonths}
            onChange={(e) => setGrantMonths(Number(e.target.value))}
          >
            {[1, 3, 6, 12].map((m) => (
              <MenuItem key={m} value={m}>
                {m} мес
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantUser(null)}>Отмена</Button>
          <Button variant="contained" onClick={grantSubscription}>
            Выдать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
